import { useEffect, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import Lenis from "lenis";
import Index from "./pages/Index.tsx";

// Lazy-loaded routes for code splitting
const ILoveSerumInfo = lazy(() => import("./pages/ILoveSerumInfo.tsx"));
const DermikaGoldInfo = lazy(() => import("./pages/DermikaGoldInfo.tsx"));
const BielendaEnergyBoostInfo = lazy(() => import("./pages/BielendaEnergyBoostInfo.tsx"));
const BielendaSatinInfo = lazy(() => import("./pages/BielendaSatinInfo.tsx"));
const GenericPurchase = lazy(() => import("./pages/GenericPurchase.tsx"));
const GenericProductInfo = lazy(() => import("./pages/GenericProductInfo.tsx"));
const Shop = lazy(() => import("./pages/Shop.tsx"));
const Checkout = lazy(() => import("./pages/Checkout.tsx"));
const Careers = lazy(() => import("./pages/Careers.tsx"));
const OurStory = lazy(() => import("./pages/OurStory.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsOfService = lazy(() => import("./pages/TermsOfService.tsx"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy.tsx"));
const PriceManager = lazy(() => import("./pages/PriceManager.tsx"));
const ImageManager = lazy(() => import("./pages/ImageManager.tsx"));
const VideoManager = lazy(() => import("./pages/VideoManager.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

let lenisInstance: Lenis | null = null;

const LenisProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisInstance = lenis;

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);

  return <>{children}</>;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    if (lenisInstance) {
      lenisInstance.scrollTo(0, { immediate: true });
    }
  }, [pathname]);
  return null;
};

const App = () => (
  <LanguageProvider>
    <CartProvider>
      <LenisProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/our-story" element={<OurStory />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/cookies" element={<CookiePolicy />} />
                  <Route path="/i-love-serum/info" element={<ILoveSerumInfo />} />
                  <Route path="/dermika-gold-24k-cream/info" element={<DermikaGoldInfo />} />
                  <Route path="/bielenda-energy-boost-vitc-serum/info" element={<BielendaEnergyBoostInfo />} />
                  <Route path="/bielenda-spf50-cream/info" element={<BielendaSatinInfo />} />
                  <Route path="/prices" element={<PriceManager />} />
                  <Route path="/images" element={<ImageManager />} />
                  <Route path="/videos" element={<VideoManager />} />
                  <Route path="/:slug/purchase" element={<GenericPurchase />} />
                  <Route path="/:slug/info" element={<GenericProductInfo />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </LenisProvider>
    </CartProvider>
  </LanguageProvider>
);

export default App;
